import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Modal, Button, Input, Select, Alert, Space, Spin, Progress, message } from "antd";
import axios from "axios";
import Cookies from "universal-cookie";

const { Option } = Select;

export default function ClosingQuestionnaireModal({
  open,
  onClose,          // (didSubmit:boolean) => void
  tranId,           // required
  onSubmitted,      // optional callback after successful submit
}) {
  const cookies = new Cookies();
  const token = cookies.get("token");
  const cookieUserId = cookies.get("userId");
  const authHeaders = { Authorization: `Bearer ${token}` };

  const [loading, setLoading] = useState(true);
  const [wo, setWo] = useState(null);
  const [template, setTemplate] = useState(null);   // { _id, title, questions: [...] }
  const [answers, setAnswers] = useState({});       // { [uuid]: string }
  const [submitting, setSubmitting] = useState(false);

  const workOrderId = wo?._id || wo?.workOrderId || wo?.orderId || "";
  const disabled = wo?.reconciliationStatus === "Completed";

  const getUserIdValue = useCallback(() => {
    if (cookieUserId) return cookieUserId;
    try {
      const raw = localStorage.getItem("userData");
      if (raw) {
        const parsed = JSON.parse(raw);
        return parsed?.user?.id || parsed?.id || "";
      }
    } catch { }
    return "";
  }, [cookieUserId]);

  // ---- API helpers (only needed routes) ----
  const fetchWorkOrder = async () => {
    const res = await axios.get(
      `${process.env.REACT_APP_API}/workOrders/tranId/${tranId}`,
      { headers: authHeaders }
    );
    return res.data?.workOrder || res.data?.data || res.data;
  };

  const fetchTemplateList = async () => {
    const res = await axios.get(
      `${process.env.REACT_APP_API}/genericTemplate/list?page=1&perPage=10000`,
      { headers: authHeaders }
    );
    // shape: { status, data: { list: [...] } }
    const list = res?.data?.data?.list || [];
    return Array.isArray(list) ? list : [];
  };

  const fetchTemplateDetail = async (id) => {
    const res = await axios.get(
      `${process.env.REACT_APP_API}/genericTemplate/${id}`,
      { headers: authHeaders }
    );
    // shape: { status, data: { genericTemplate: {...} } }
    const doc = res?.data?.data?.genericTemplate || {};
    const qs = Array.isArray(doc?.customGeneric) ? doc.customGeneric : [];
    return { _id: doc?._id || id, title: doc?.title || "Closing Questionnaire", questions: qs };
  };

  // ---- bootstrap ----
  const init = useCallback(async () => {
    if (!open || !tranId) return;
    setLoading(true);
    try {
      const woResp = await fetchWorkOrder();
      setWo(woResp);

      // List -> first -> detail (you have only one)
      const list = await fetchTemplateList();
      const picked = (list && list.length) ? list[0] : null;
      if (!picked) {
        setTemplate(null);
        setLoading(false);
        return;
      }
      const detail = await fetchTemplateDetail(picked._id || picked.id);
      setTemplate(detail);

      // fresh form
      setAnswers({});
    } catch (e) {
      setTemplate(null);
      message.error("Failed to load closing questionnaire.");
    } finally {
      setLoading(false);
    }
  }, [open, tranId]);

  useEffect(() => {
    init();
  }, [init]);

  // ---- validation ----
  const questions = useMemo(() => template?.questions || [], [template]);

  const validate = () => {
    const missing = [];
    for (const q of questions) {
      const required = String(q.require_response || q.requireResponse || "").toLowerCase() === "true";
      if (!required) continue;
      const val = (answers[q.uuid] ?? "").toString().trim();
      if (!val) missing.push(q.question || "Required field");
    }
    return missing.length ? `Please answer required fields:\n- ${missing.join("\n- ")}` : "";
  };

  // ---- submit ----
  const onSubmit = async () => {
    const err = validate();
    if (err) return message.error(err);

    const userId = getUserIdValue();
    if (!userId || !tranId || !workOrderId || !template?._id) {
      return message.error("Missing required fields (userId / tranId / workOrderId / template).");
    }

    const fd = new FormData();
    fd.append("genericId", template._id);
    fd.append("userId", userId);
    fd.append("tranId", tranId);
    fd.append("workOrderId", workOrderId);

    questions.forEach((q, i) => {
      fd.append(`answer[${i}][uuid]`, q.uuid || "");
      fd.append(`answer[${i}][question]`, q.question || "");
      fd.append(`answer[${i}][answer]`, (answers[q.uuid] ?? "").toString());
    });

    try {
      setSubmitting(true);
      await axios.post(`${process.env.REACT_APP_API}/genericAnswer`, fd, { headers: authHeaders });
      message.success("Closing questionnaire submitted.");
      onSubmitted?.();
      onClose?.(true);
    } catch (e) {
      message.error(e?.response?.data?.message || "Failed to submit closing questionnaire.");
    } finally {
      setSubmitting(false);
    }
  };

  // ---- render controls ----
  const renderControl = (q) => {
    const required = String(q.require_response || q.requireResponse || "").toLowerCase() === "true";
    const type = String(q.type || "").toLowerCase();

    if (type === "dropdown") {
      // options may be object like {"0":"Yes","1":"No"} or an array
      const opts = Array.isArray(q.options) ? q.options : Object.values(q.options || {});
      return (
        <Select
          style={{ width: "100%" }}
          value={answers[q.uuid] ?? undefined}
          onChange={(v) => setAnswers((prev) => ({ ...prev, [q.uuid]: v }))}
          disabled={disabled}
          placeholder="Please select"
        >
          {opts.map((v, i) => (
            <Option key={`${q.uuid}-${i}`} value={v}>{v}</Option>
          ))}
        </Select>
      );
    }

    return (
      <Input.TextArea
        rows={3}
        value={answers[q.uuid] ?? ""}
        onChange={(e) => setAnswers((prev) => ({ ...prev, [q.uuid]: e.target.value }))}
        disabled={disabled}
        placeholder={required ? "Required" : ""}
      />
    );
  };

  // progress (visual only: answered required vs total required)
  const [answeredReq, totalReq] = useMemo(() => {
    const reqQs = questions.filter(q => String(q.require_response || q.requireResponse || "").toLowerCase() === "true");
    const answered = reqQs.filter(q => String(answers[q.uuid] || "").trim().length > 0).length;
    return [answered, reqQs.length];
  }, [questions, answers]);

  return (
    <Modal
      open={open}
      maskClosable={false}
      closable={false}
      keyboard={false}
      onCancel={() => { }}
      title="Closing Questionnaire"
      width={800}
      destroyOnClose
      footer={
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <Space />
          <Space>
            <Button onClick={() => onClose?.(false)}>Exit</Button>
            <Button
              type="primary"
              onClick={onSubmit}
              loading={submitting}
              disabled={disabled || loading || !questions.length}
            >
              Submit
            </Button>
          </Space>
        </div>
      }
    >
      {loading ? (
        <div style={{ padding: 16 }}><Spin /></div>
      ) : !questions.length ? (
        <Alert type="info" message="No closing questionnaire template found." showIcon />
      ) : (
        <>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12 }}>
            <div style={{ fontWeight: 600 }}>
              {template?.title || "Closing Questionnaire"}
            </div>
            {totalReq > 0 && (
              <div style={{ minWidth: 220 }}>
                <Progress
                  status="active"
                  percent={Math.round((answeredReq / Math.max(totalReq, 1)) * 100)}
                  format={() => `Answered ${answeredReq} of ${totalReq} required`}
                />
              </div>
            )}
          </div>

          {questions.map((q) => (
            <div key={q.uuid} style={{ marginBottom: 16 }}>
              <div style={{ fontWeight: 600, marginBottom: 6 }}>
                {q.question || "Question"}
                {String(q.require_response || q.requireResponse || "").toLowerCase() === "true" && (
                  <span style={{ color: "#d00", marginLeft: 6 }}>*</span>
                )}
              </div>
              {renderControl(q)}
            </div>
          ))}
        </>
      )}
    </Modal>
  );
}

